import React from 'react';
import {
    FOCUS_CART_SHORTCUT,
    LINE_DISCOUNT_SHORTCUT,
    PAY_SHORTCUT,
    SALE_DISCOUNT_SHORTCUT,
    useShortcut,
    VOID_SHORTCUT,
} from '@/components/shortcuts';
import { priceLine, toGel } from '../../discountMath';
import { formatGel } from '../../sellerMeta';
import {
    useSeller,
    type CartLine,
    type DiscountProblem,
} from '../../useSeller';
import { useShift } from '../../shift/useShift';
import { useHomeFocus } from '../../HomeFocusProvider';
import { usePaymentFlow } from '../../payment/usePaymentFlow';
import { useDiscountDialog } from '../discountDialog';
import { useCartKeyboardNav } from './useCartKeyboardNav';
import { useVoidConfirm } from './useVoidConfirm';

/**
 * A message for the seller as an i18n key and its values, never translated
 * here: why a cart shortcut could not run, or why a discount blocks payment.
 */
export type CartNotice = {
    key: string;
    values?: Record<string, string>;
};

/** One cart line with what it shows (GEL): its list total and what it costs after its own discount. */
export type CartLineView = {
    line: CartLine;
    listTotal: number;
    /** List total less the line's own discount; equals `listTotal` without one. */
    total: number;
    /** The line's discount no longer fits it (`discountProblem`). */
    isDiscountInvalid: boolean;
};

type UseCartPanelParams = {
    /** Shows a refused shortcut's notice; the section translates it. */
    onNotice: (notice: CartNotice) => void;
};

const CART_EMPTY: CartNotice = { key: 'seller.cartEmpty' };
const NEED_SHIFT: CartNotice = { key: 'seller.shift.needShiftToSell' };

/**
 * What blocks payment while a discount no longer fits, naming the figure it
 * no longer fits: the line's list total, or what the lines leave for the
 * sale discount.
 */
const discountNoticeOf = (
    problem: DiscountProblem,
    lines: CartLineView[],
    afterLines: number,
    hasSaleDiscount: boolean,
): CartNotice => {
    if (problem.kind === 'lineTooLarge') {
        const view = lines.find(
            ({ line }) => line.variant.variantId === problem.variantId,
        );
        return view
            ? {
                  key: 'seller.cartDiscount.problemLine',
                  values: {
                      name: view.line.product.name,
                      amount: formatGel(view.listTotal),
                  },
              }
            : { key: 'seller.cartDiscount.problemValue' };
    }
    if (problem.kind === 'totalNotPositive') {
        return hasSaleDiscount
            ? {
                  key: 'seller.cartDiscount.problemSale',
                  values: { amount: formatGel(afterLines) },
              }
            : { key: 'seller.cartDiscount.problemLines' };
    }
    return { key: 'seller.cartDiscount.problemValue' };
};

/**
 * The cart panel's owning hook: its lines and totals from `useSeller`, the
 * keyboard layer (`useCartKeyboardNav`), the void confirm
 * (`useVoidConfirm`), the discount dialog (`useDiscountDialog`) and the
 * payment modal (`usePaymentFlow`), and the panel's shortcuts. `F4` focuses
 * the last line, `F6` opens the line discount for the focused line (the last
 * one when no line has focus), `F7` the sale discount, `Ctrl+Delete` opens
 * the void confirm (the Void button and the header's clear-cart control open
 * the same dialog), and `F9` opens payment from anywhere on the sale screen
 * (bos-seller#58). Each one pressed while it cannot run hands `onNotice` the
 * reason instead — "cart is empty", or "open a shift first" or the discount
 * notice for `F9` — as a key, never translated here.
 *
 * Discounts (SEL-100): each line comes with its list total and its total
 * after its own discount (`lines`); a discount that no longer fits the cart
 * (`discountProblem`) produces `discountNotice`, hides the total and blocks
 * Pay, Hold and `F9` until the seller edits or clears it.
 */
export const useCartPanel = ({ onNotice }: UseCartPanelParams) => {
    const {
        cart,
        cartCount,
        search,
        setSearch,
        setLineQnt,
        removeLine,
        clearCart,
        saleDiscount,
        setLineDiscount,
        setSaleDiscount,
        discountProblem,
        subtotal,
        lineDiscountTotal,
        saleDiscountAmount,
        total,
    } = useSeller();
    const { isOpen: isShiftOpen } = useShift();
    const { focusHome, focusSearch } = useHomeFocus();
    const payment = usePaymentFlow({ focusHome });
    const voidConfirm = useVoidConfirm({ clearCart, focusHome });
    const discount = useDiscountDialog({
        cart,
        saleDiscount,
        setLineDiscount,
        setSaleDiscount,
        focusHome,
    });
    const { registerItemRef, focusLastLine, getFocusedLine, listProps } =
        useCartKeyboardNav({
            cart,
            setLineQnt,
            removeLine,
            search,
            setSearch,
            focusHome,
            focusSearch,
        });

    const isEmpty = cart.length === 0;

    const lines = React.useMemo(
        (): CartLineView[] =>
            cart.map((line) => {
                const priced = priceLine({
                    unitPrice: line.variant.price,
                    quantity: line.qnt,
                    discount: line.discount,
                });
                return {
                    line,
                    listTotal: toGel(priced.listTotalTetri),
                    total: toGel(priced.afterDiscountTetri),
                    isDiscountInvalid:
                        discountProblem?.variantId === line.variant.variantId,
                };
            }),
        [cart, discountProblem],
    );

    const discountNotice = React.useMemo(
        () =>
            discountProblem === null
                ? null
                : discountNoticeOf(
                      discountProblem,
                      lines,
                      Math.round((subtotal - lineDiscountTotal) * 100) / 100,
                      saleDiscount !== null,
                  ),
        [discountProblem, lines, subtotal, lineDiscountTotal, saleDiscount],
    );

    const canPay = !isEmpty && isShiftOpen && discountNotice === null;

    useShortcut(FOCUS_CART_SHORTCUT.id, focusLastLine, {
        enabled: !isEmpty,
        onUnavailable: () => onNotice(CART_EMPTY),
    });

    useShortcut(
        LINE_DISCOUNT_SHORTCUT.id,
        () => {
            const target =
                getFocusedLine() ?? cart[cart.length - 1]?.variant.variantId;
            if (target !== undefined) {
                discount.openLine(target);
            }
        },
        {
            enabled: !isEmpty,
            onUnavailable: () => onNotice(CART_EMPTY),
        },
    );

    useShortcut(SALE_DISCOUNT_SHORTCUT.id, discount.openSale, {
        enabled: !isEmpty,
        onUnavailable: () => onNotice(CART_EMPTY),
    });

    useShortcut(VOID_SHORTCUT.id, voidConfirm.open, {
        enabled: !isEmpty,
        onUnavailable: () => onNotice(CART_EMPTY),
    });

    useShortcut(PAY_SHORTCUT.id, payment.openModal, {
        enabled: canPay,
        onUnavailable: () =>
            onNotice(
                isEmpty
                    ? CART_EMPTY
                    : !isShiftOpen
                      ? NEED_SHIFT
                      : (discountNotice ?? CART_EMPTY),
            ),
    });

    return {
        cart,
        lines,
        cartCount,
        subtotal,
        lineDiscountTotal,
        saleDiscount,
        saleDiscountAmount,
        total,
        discountNotice,
        isSaleDiscountInvalid:
            saleDiscount !== null &&
            discountProblem !== null &&
            discountProblem.variantId === undefined,
        /** A line's discount no longer fits, so the line-discount sum is unknown. */
        isLineDiscountInvalid: discountProblem?.variantId !== undefined,
        isEmpty,
        canPay,
        registerItemRef,
        listProps,
        onLineQnt: setLineQnt,
        onRemoveLine: removeLine,
        onLineDiscount: discount.openLine,
        onSaleDiscount: discount.openSale,
        payment,
        voidConfirm,
        discount,
    };
};

export type CartPanelState = ReturnType<typeof useCartPanel>;
